import type { Banner } from '~/types'
import {
  bannerImageProbeKey,
  getBannerImageInfo,
  type BannerImageVariant,
} from '~/utils/bannerImage'

type ImageDimensions = {
  width: number
  height: number
}

export function getBannerImageDimensions(
  banner: Banner,
  isMobile: boolean,
  probedDimensions: Record<string, ImageDimensions>,
): ImageDimensions | null {
  const info = getBannerImageInfo(banner, isMobile)
  if (!info) return null

  if (info.width && info.height) {
    return { width: info.width, height: info.height }
  }

  return probedDimensions[bannerImageProbeKey(banner.id, info.variant)] ?? null
}

export function useHeroBannerHeight(
  banners: Ref<Banner[]>,
  fallbackHeightDesktop: MaybeRefOrGetter<number>,
  fallbackHeightMobile?: MaybeRefOrGetter<number>,
) {
  const { mediaUrl } = useMediaUrl()
  const isMobile = useMediaQuery('(max-width: 767px)')

  const activeIndex = ref(0)
  const probedDimensions = ref<Record<string, ImageDimensions>>({})

  function probeBannerVariant(banner: Banner, variant: BannerImageVariant, src?: string | null) {
    if (!src) return

    const key = bannerImageProbeKey(banner.id, variant)
    if (probedDimensions.value[key]) return

    const img = new Image()
    img.onload = () => {
      if (img.naturalWidth <= 0 || img.naturalHeight <= 0) return

      probedDimensions.value = {
        ...probedDimensions.value,
        [key]: {
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
      }
    }
    img.src = mediaUrl(src)
  }

  function probeBannerImage(banner: Banner) {
    probeBannerVariant(banner, 'desktop', banner.image)
    probeBannerVariant(banner, 'mobile', banner.mobileImage)
  }

  const maxHeroHeight = computed(() => {
    const desktop = toValue(fallbackHeightDesktop)
    const mobileConfigured = fallbackHeightMobile == null ? undefined : toValue(fallbackHeightMobile)
    const configured = isMobile.value
      ? (mobileConfigured ?? desktop)
      : desktop
    const fallback = isMobile.value ? 360 : 420
    return Number.isFinite(configured) && configured > 0 ? configured : fallback
  })

  const heroContainerStyle = computed(() => {
    const height = `${maxHeroHeight.value}px`
    // Fixed height from admin (hero_height / hero_height_mobile).
    // Image fills with object-cover.
    return {
      height,
      maxHeight: height,
    }
  })

  function onSlideSelect(index: number) {
    activeIndex.value = index
  }

  watch(banners, (list) => {
    if (activeIndex.value >= list.length) {
      activeIndex.value = 0
    }
    list.forEach(probeBannerImage)
  }, { immediate: true })

  watch(isMobile, () => {
    banners.value.forEach(probeBannerImage)
  })

  return {
    activeIndex,
    heroContainerStyle,
    onSlideSelect,
    isMobile,
    registerProbedDimensions: (key: string, dimensions: ImageDimensions) => {
      probedDimensions.value = {
        ...probedDimensions.value,
        [key]: dimensions,
      }
    },
    getBannerImageDimensions: (banner: Banner) =>
      getBannerImageDimensions(banner, isMobile.value, probedDimensions.value),
  }
}
